import React, { useEffect, useState } from 'react';
import styles from './business-case.module.css';

interface BusinessCaseData {
  ProjectId: string;
  ImpactAnalysis?: string;
  ResourceRequirements?: string;
  RiskAssessment?: string;
  TechnicalApproach?: string;
  SuccessCriteria?: string;
}

interface BusinessCaseFormProps {
  projectId: string;
  projectTitle?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function BusinessCaseForm({
  projectId,
  projectTitle = 'Project',
  onSuccess,
  onCancel,
}: BusinessCaseFormProps) {
  const [data, setData] = useState<BusinessCaseData>({
    ProjectId: projectId,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchBusinessCase();
  }, [projectId]);

  const fetchBusinessCase = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/projects/${projectId}/business-case`);
      const result = await response.json();

      if (result.success && result.data) {
        setData(result.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch business case:', err);
      // It's okay if it doesn't exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(`/projects/${projectId}/business-case`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('Business case saved successfully!');
        setTimeout(() => {
          onSuccess?.();
        }, 1500);
      } else {
        setError(result.error || 'Failed to save business case');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save business case');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading business case...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Business Case</h1>
        <p className={styles.subtitle}>Build a strong business case for {projectTitle}</p>
      </div>

      {error && (
        <div className={styles.error}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className={styles.success}>
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="impactAnalysis" className={styles.label}>
            Impact Analysis <span className={styles.required}>*</span>
          </label>
          <textarea
            id="impactAnalysis"
            value={data.ImpactAnalysis || ''}
            onChange={e => handleChange('ImpactAnalysis', e.target.value)}
            placeholder="Describe the expected impact of this project. What problems does it solve? What value does it create?"
            className={styles.textarea}
            rows={4}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="resourceRequirements" className={styles.label}>
            Resource Requirements <span className={styles.required}>*</span>
          </label>
          <textarea
            id="resourceRequirements"
            value={data.ResourceRequirements || ''}
            onChange={e => handleChange('ResourceRequirements', e.target.value)}
            placeholder="What resources are needed? Include people, budget, tools, infrastructure, etc."
            className={styles.textarea}
            rows={4}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="riskAssessment" className={styles.label}>
            Risk Assessment <span className={styles.required}>*</span>
          </label>
          <textarea
            id="riskAssessment"
            value={data.RiskAssessment || ''}
            onChange={e => handleChange('RiskAssessment', e.target.value)}
            placeholder="What are the key risks? How will you mitigate them?"
            className={styles.textarea}
            rows={4}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="technicalApproach" className={styles.label}>
            Technical Approach
          </label>
          <textarea
            id="technicalApproach"
            value={data.TechnicalApproach || ''}
            onChange={e => handleChange('TechnicalApproach', e.target.value)}
            placeholder="Describe the high-level technical approach. Architecture decisions, technologies, integration points."
            className={styles.textarea}
            rows={4}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="successCriteria" className={styles.label}>
            Success Criteria
          </label>
          <textarea
            id="successCriteria"
            value={data.SuccessCriteria || ''}
            onChange={e => handleChange('SuccessCriteria', e.target.value)}
            placeholder="How will you measure success? Define key metrics and acceptance criteria."
            className={styles.textarea}
            rows={4}
          />
        </div>

        <div className={styles.formActions}>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className={`${styles.button} ${styles.buttonSecondary}`}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save Business Case'}
          </button>
        </div>
      </form>
    </div>
  );
}
